// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@conklin/App/Store/Reducers";
import ProductImageCarousel, { ProductImageCarouselStyles } from "@conklin/Overrides/Components/ProductImageCarousel";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import setSelectedImageIndex from "@insite/client-framework/Store/Pages/ProductDetails/Handlers/SetSelectedImageIndex";
import { DocumentModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useMemo } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    scrollingBehavior = "scrollingBehavior",
    numberOfVisibleThumbnails = "numberOfVisibleThumbnails",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.scrollingBehavior]: string;
        [fields.numberOfVisibleThumbnails]: number;
    };
}

const mapStateToProps = (state: CustomAppState, ownProps: HasProduct) => ({
    selectedImageIndex: state.pages.productDetails.selectedImageIndex,
});

const mapDispatchToProps = {
    setSelectedImageIndex,
};

type Props = OwnProps & HasProduct & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductDetailsImageCarouselStyles {
    imageCarousel?: ProductImageCarouselStyles;

    /**
     * @deprecated Use imageCarousel styles instead.
     */
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    image?: LazyImageProps;
    selectedImage?: LazyImageProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonProps;
}

export const productDetailsImageCarouselStyles: ProductDetailsImageCarouselStyles = {};

const styles = productDetailsImageCarouselStyles;

const ProductDetailsImageCarousel = ({
    product,
    selectedImageIndex,
    setSelectedImageIndex,
    fields: { scrollingBehavior, numberOfVisibleThumbnails },
}: Props) => {
    const selectImageHandler = (index: number) => {
        setSelectedImageIndex({ index });
    };

    // XNG-Change Handle videos.
    const videoAssets: DocumentModel[] = useMemo(
        () => (product.documents && product.documents?.filter(doc => doc.documentType.toLowerCase() === "video")) || [],
        [product.images],
    );

    // XNG-Changes: remove <= 0, handled videos
    if (!product || ((!product.images || product.images.length === 0) && (!videoAssets || videoAssets.length === 0))) {
        return null;
    }

    return (
        <ProductImageCarousel
            scrollingBehavior={scrollingBehavior}
            numberOfVisibleThumbnails={numberOfVisibleThumbnails}
            selectedIndex={selectedImageIndex}
            onSelectImage={selectImageHandler}
            extendedStyles={styles.imageCarousel}
            {...{ videos: videoAssets }}
        />
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps, mapDispatchToProps)(ProductDetailsImageCarousel)),
    definition: {
        displayName: "Image Carousel",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
        fieldDefinitions: [
            // XNG-Change: will not use since we have vertical style for desktop view and horizontal for tablet and mobile
            // {
            //     name: fields.scrollingBehavior,
            //     displayName: "Scrolling Behavior",
            //     editorTemplate: "RadioButtonsField",
            //     defaultValue: "vertical",
            //     horizontal: true,
            //     fieldType: "General",
            //     options: [
            //         {
            //             displayName: "Horizontal",
            //             value: "horizontal",
            //         },
            //         {
            //             displayName: "Vertical",
            //             value: "vertical",
            //         },
            //     ],
            //     sortOrder: 0,
            // },
            {
                name: fields.numberOfVisibleThumbnails,
                displayName: "Number Of Visible Thumbnails",
                editorTemplate: "IntegerField",
                min: 1,
                max: 20,
                // XNG-Change
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 1,
            },
        ],
    },
};

export default widgetModule;
